import clsx from 'clsx';
import { SkipToContent } from '@/components/skip-to-content';
import { useAuthQuery } from '@/services/auth';
import { useListWorkspaces } from '@/features/workspaces/hooks';
import {
  announceCallbackComplete,
  clearAttemptedTransitionUrl,
  getAttemptedTransitionUrl,
  setAttemptedTransitionUrl,
  subscribeToRefreshAuth,
  subscribeToRefreshWorkspaces,
} from '@customerio/hydra-nav-state';
import { PermissionsProvider } from '@customerio/hydra-permissions/react';
import { type RoleDefinition } from '@customerio/hydra-permissions/roles';
import { isStandardRole, useRoleDefinitions } from '@/features/roles/hook';
import { Fragment, useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { appendMasqParam } from '@/utils/masq';
import { MainNavigation } from './components/main-navigation';
import { MainNavigationNew } from './components/main-navigation-new';
import { MainNavigationWrapper, NAV_COLLAPSED_LOCAL_STORAGE_KEY } from './components/main-navigation-new/wrapper';
import { isEmptyString } from '@/utils/string';
import { CommandDialog } from '@/features/command-dialog';
import { RecordPerformanceMetric } from '@/components/record-performance-metric';
import { RocketMissionControl } from '@/components/rocket-mission-control';
import { useActiveBundle } from '@/context/active-bundle';
import { CurrentWorkspaceProvider, useCurrentWorkspace } from '@/context/current-workspace';
import { PerformanceMetric, PerformanceProvider } from '@/context/performance';
import { SidebarRoot } from '@/features/sidebar';
import { HelpSidebar } from '@/features/help-sidebar';
import { GlobalCSSVariable, useThemeContext } from '@/context/theme';
import { appContainerFadedOutStore, appContainerOutletRenderedStore } from '@/stores/root';
import { AccountState } from '@/services/auth/types';
import { analytics } from '@/services/analytics/client';
import { useIsSettings } from '@/utils/navigation';
import { AppBundle } from '@/utils/app-bundles';
import { useSampleWorkspace } from '@/features/workspaces/sample-workspace';
import { VideoLibraryProvider } from '@/features/video-library';
import tracking from '@/utils/tracking-scripts';
import type { PermissionsUser } from '@customerio/hydra-permissions/dist/validation';
import { UserMasqPermission } from '@customerio/hydra-permissions/masq';
import { InlineStack, Stack, PageLoader, SideNavProvider } from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { ErrorBoundary } from 'react-error-boundary';
import { SettingsNavigation } from './components/settings-navigation';
import { SettingsNavigationNew } from './components/settings-navigation-new';
import { TopNavigation } from './components/top-navigation';
import { TopNavigationNew } from './components/top-navigation-new';
import { SampleWorkspaceBorder } from './components/top-navigation/components/sample-workspace';
import { WorkspaceStripe } from './components/workspace-stripe';
import {
  sentrySetDataCenter,
  sentrySetUser,
  setSentryFullStorySessionUrl,
  setSentryMasqueradingContext,
} from '@/utils/sentry';
import { Navigate } from '@/utils/navigate';
import { isDemoAccount } from '@/utils/demo-accounts';
import { getWorkspaceIdFromUrl } from '@/context/current-workspace';
import { AuthProvider } from '@/services/auth/context';
import type { AccountPermission } from '@customerio/hydra-permissions';
import { accountPermissionsForRole } from '@customerio/hydra-permissions/roles';
import { Region } from '@/services/auth/types';
import { ROCKET_DISMISSED_KEY } from '@/components/rocket';
import { AssistantFooter, CopilotSessionProvider } from '@/features/copilot';
import { AIShimmer } from '@/components/ai-shimmer';
import { getStorageValue } from '@/hooks/browser/use-local-storage';
import { hasFeatureFlag, FeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { MasqueradeBanner } from '@/features/masquerade-banner';
import * as styles from './index.css';

let didIdentify = false;

function AuthenticatedContent() {
  const auth = useAuthQuery();
  const workspaces = useListWorkspaces();
  const currentWorkspace = useCurrentWorkspace();
  const appContainerFadedOut = useStore(appContainerFadedOutStore);
  const appContainerOutletRendered = useStore(appContainerOutletRenderedStore);
  const themeContext = useThemeContext();
  const location = useLocation();
  const { isSettingsPage, isAnySettingsPage } = useIsSettings();
  const { isSampleWorkspace } = useSampleWorkspace();
  const roleDefinitions = useRoleDefinitions();
  const activeApp = useActiveBundle();

  const is2026NavFlagOn = useFeatureFlag(FeatureFlag.LayoutRefresh2026);
  const showMainNav = !isAnySettingsPage && !is2026NavFlagOn;
  const showMainNavNew = !isAnySettingsPage && is2026NavFlagOn;
  const showSettingSideNav = isAnySettingsPage;

  const currentWorkspaceIdRef = useRef(currentWorkspace?.id != null ? `${currentWorkspace.id}` : null);
  const shouldScheduleAuthRefresh = useRef(false);
  const refreshAuthCallbackId = useRef<string | null>(null);

  const isSideNavDefaultCollapsed = getStorageValue<boolean>(NAV_COLLAPSED_LOCAL_STORAGE_KEY, false);

  useEffect(() => {
    // Set the top nav height to 42px to make sure child apps
    // have the correct CSS variable values
    themeContext.setVariable(GlobalCSSVariable.TopNavHeight, is2026NavFlagOn ? '42px' : '40px');
  }, [is2026NavFlagOn, themeContext]);

  useEffect(() => {
    return subscribeToRefreshAuth(async (event) => {
      const workspaceIdFromUrl = getWorkspaceIdFromUrl(window.location.href);
      const currentWorkspaceId = currentWorkspaceIdRef.current != null ? `${currentWorkspaceIdRef.current}` : null;

      // If the workspace id in the URL is different from the current workspace id
      // from the useCurrentWorkspace hook,
      // schedule an auth.refetch once the URL matches the current workspace id.
      // This is necessary because the useAuth hook reads the workspace ID from the URL,
      // but the URL may not update immediately, causing the auth data to be stale.
      if (currentWorkspaceId != null && currentWorkspaceId !== workspaceIdFromUrl) {
        shouldScheduleAuthRefresh.current = true;
        refreshAuthCallbackId.current = event.detail.id;
      } else {
        try {
          await auth.refetch();
        } finally {
          announceCallbackComplete(event.detail.id);
          refreshAuthCallbackId.current = null;
        }
      }
    });
  }, []);

  useEffect(() => {
    const currentWorkspaceId = currentWorkspace?.id != null ? `${currentWorkspace.id}` : null;
    currentWorkspaceIdRef.current = currentWorkspaceId;
  }, [currentWorkspace?.id]);

  useEffect(() => {
    const workspaceIdFromUrl = getWorkspaceIdFromUrl(window.location.href);

    // If there is an auth refresh scheduled and the workspace IDs finally match,
    // call .refetch on the auth hook query.
    if (shouldScheduleAuthRefresh.current && currentWorkspaceIdRef.current === workspaceIdFromUrl) {
      shouldScheduleAuthRefresh.current = false;
      const cb = async () => {
        try {
          await auth.refetch();
        } finally {
          if (refreshAuthCallbackId.current != null) {
            announceCallbackComplete(refreshAuthCallbackId.current);
            refreshAuthCallbackId.current = null;
          }
        }
      };
      cb();
    }
  }, [location.pathname, currentWorkspace?.id]);

  const isRefreshingAuthSampleWorkspace = useRef(false);
  useEffect(() => {
    // Switching to the sample workspace doesn't trigger
    // `subscribeToRefreshAuth`. If the current user doesn't have a role
    // for the sample workspace, refresh the auth data.
    if (currentWorkspace?.id != null && isSampleWorkspace(currentWorkspace.id)) {
      const workspaceId = currentWorkspace.id;
      const hasRoleForSampleWorkspace = auth.data?.user?.roles?.some((r) => r.workspace_id === workspaceId) ?? false;

      if (!hasRoleForSampleWorkspace && !auth.isLoading && !isRefreshingAuthSampleWorkspace.current) {
        const cb = async () => {
          isRefreshingAuthSampleWorkspace.current = true;
          try {
            await auth.refetch();
          } finally {
            isRefreshingAuthSampleWorkspace.current = false;
          }
        };
        cb();
      }
    }
  }, [isSampleWorkspace, currentWorkspace?.id, auth.data?.user?.roles, auth.isLoading]);

  useEffect(() => {
    return subscribeToRefreshWorkspaces(async (event) => {
      try {
        await workspaces.refetch();
      } finally {
        announceCallbackComplete(event.detail.id);
      }
    });
  }, []);

  useEffect(() => {
    const showTopColor = !isSettingsPage && currentWorkspace != null && !isEmptyString(currentWorkspace.color);

    if (showTopColor && currentWorkspace != null) {
      themeContext.setVariable(GlobalCSSVariable.WorkspaceBorderColor, currentWorkspace.color);
      themeContext.setVariable(GlobalCSSVariable.WorkspaceBorderHeight, '6px');
    } else {
      themeContext.setVariable(GlobalCSSVariable.WorkspaceBorderHeight, '0px');
    }
  }, [currentWorkspace, isSettingsPage]);

  // Additional Sentry setup once the user is authenticated
  useEffect(() => {
    if (auth.isSuccess === false || auth.data == null) {
      return;
    }

    const { account, user } = auth.data;

    if (account == null || user == null) {
      return;
    }

    sentrySetUser(account, user);
    sentrySetDataCenter(account.region === Region.EU ? 'eu' : 'us');
    setSentryFullStorySessionUrl();
    setSentryMasqueradingContext(account.is_masq);
  }, [auth.data, auth.isSuccess]);

  // inject tracking scripts once the user is authenticated
  useEffect(() => {
    if (auth.isSuccess === true && auth.data != null && !didIdentify) {
      didIdentify = true;
      const { user } = auth.data;

      // identify
      void analytics?.identify(`${user?.id}`, {
        id: `${user?.id}`,
        email: user?.email,
      });
    }
  }, [auth.data, auth.isSuccess]);

  useEffect(() => {
    void analytics?.page();
  }, [location]);

  // clean tracking scripts + analytics once the component is unmounted
  useEffect(() => {
    return () => {
      tracking.cleanup();
      void analytics?.reset();
    };
  }, []);

  // Using useMemo to make sure the attempted url setting runs before rendering,
  // i.e. before the Navigate component is rendered.
  useMemo(() => {
    if (auth.isLoading === true) {
      return;
    }

    if (auth.data == null) {
      const attemptedUrl = window.location.href.replace(/\/$/, '');
      if (getAttemptedTransitionUrl() == null && attemptedUrl !== window.location.origin) {
        setAttemptedTransitionUrl(attemptedUrl);
      }
    } else {
      clearAttemptedTransitionUrl();
    }
  }, [auth.data, auth.isLoading]);

  if (auth.isLoading === true) {
    return <PageLoader />;
  }

  if (auth.data == null || auth.data?.account == null || auth.data?.user == null) {
    return <Navigate to="/login" saveCurrentUrl={true} />;
  }

  if (workspaces.isLoading) {
    return <PageLoader />;
  }

  // Sample workspace check fixes this issue https://customerio.slack.com/archives/C3EPR6SGH/p1764344886993649
  // The exact cause hasn't been pinpointed yet, need to investigate, but this fixes the issue
  if (roleDefinitions == null && !isSampleWorkspace(currentWorkspace?.id)) {
    return <PageLoader />;
  }

  const { account, user } = auth.data;

  // Set up permissions context
  const workspaceUserRoles =
    user.roles != null
      ? user.roles.reduce((acc, userRole) => {
          acc[userRole.workspace_id] = { id: userRole.role_id, name: userRole.role_name };
          return acc;
        }, {} as Record<number, { id?: number; name: string }>)
      : null;

  const permissionsUser: PermissionsUser = {
    account_permissions: user.account_permissions,
    workspace_roles: workspaceUserRoles,
    masq_permissions: user.masq_permissions ?? UserMasqPermission.NoMasqAccess,
  };

  const permissionsWorkspaceId =
    (activeApp === AppBundle.Journeys ? currentWorkspace?.cio_workspace_id : currentWorkspace?.cdp_workspace_id) ??
    null;

  const allRoles: Record<string, RoleDefinition> = {};
  for (const role of Object.values(roleDefinitions ?? {})) {
    // custom roles don't have account permissions, so we populate what we have from the user
    const accountPermissions: Set<AccountPermission> = isStandardRole(role.name)
      ? accountPermissionsForRole(role.name)
      : new Set(user.account_permissions ?? []);

    // role names are unique
    allRoles[role.name] = {
      ...role,
      accountPermissions,
    };
  }

  // If we're masqing but the current user doesn't have masq permissions set, or
  // the permissions are "none", redirect to settings page (which is accessible
  // to all users who can masq)
  if (account.is_masq === true && (user.noMasqAccess === true || user.masqLevelNotSet === true) && !isAnySettingsPage) {
    return <Navigate to={appendMasqParam('/settings')} />;
  }

  const isTrial = account.account_state === AccountState.Free;
  let isRocketDismissed = false;
  try {
    isRocketDismissed = localStorage.getItem(ROCKET_DISMISSED_KEY) === 'true';
  } catch {
    // localStorage might fail in some edge cases. Prevent the error from crashing the app.
  }
  const shouldShowRocket =
    isTrial && !isSampleWorkspace(currentWorkspace?.id) && !isDemoAccount(account.id) && !isRocketDismissed;

  return (
    <PermissionsProvider user={permissionsUser} account={account} roles={allRoles} workspaceId={permissionsWorkspaceId}>
      <AuthProvider auth={{ account, user }}>
        <CopilotSessionProvider>
          <VideoLibraryProvider>
            <SideNavProvider defaultIsCollapsed={isSideNavDefaultCollapsed}>
              <RecordPerformanceMetric performanceMetric={PerformanceMetric.Authenticated} />
              <WorkspaceStripe />
              <MasqueradeBanner />
              <SkipToContent />
              {is2026NavFlagOn ? (
                <InlineStack alignBlock="stretch" flexGrow="1" minH="0">
                  {showMainNavNew || showSettingSideNav ? (
                    <MainNavigationWrapper isCollapsible={!showSettingSideNav}>
                      {showMainNavNew ? <MainNavigationNew /> : null}
                      {showSettingSideNav ? <SettingsNavigationNew /> : null}
                    </MainNavigationWrapper>
                  ) : null}
                  <Stack flexGrow="1" minH="0">
                    <TopNavigationNew isCollapsible={!showSettingSideNav} />
                    <div style={{ display: 'flex', minHeight: 0, flex: 1 }}>
                      {/* We will use the global command dialog for global event registration with __hydra_commands__ */}
                      <CommandDialog />
                      <div className={clsx(styles.layoutContent, styles.layoutContentLayoutRefresh)}>
                        <AIShimmer />
                        <div
                          id="embedded-app-container"
                          className={clsx(styles.layoutContentInner, styles.layoutContentInnerLayoutRefresh)}
                          style={{
                            opacity: appContainerFadedOut ? 0 : 1,
                          }}
                        >
                          {appContainerOutletRendered ? <Outlet /> : null}
                        </div>
                        {hasFeatureFlag(FeatureFlag.AiAssistantV2) && hasFeatureFlag(FeatureFlag.AiEnabled) ? (
                          <AssistantFooter />
                        ) : null}
                      </div>
                      <SidebarRoot />
                      <HelpSidebar />
                      {shouldShowRocket ? (
                        /* The Rocket component is experimental and isolated from the rest of the app,
                    So any errors that could happen within the component can be contained */
                        <ErrorBoundary fallback={<Fragment />}>
                          <RocketMissionControl />
                        </ErrorBoundary>
                      ) : null}
                    </div>
                  </Stack>
                </InlineStack>
              ) : null}
              {!is2026NavFlagOn ? (
                <>
                  <TopNavigation />
                  <div style={{ display: 'flex', minHeight: 0, flex: 1 }}>
                    {showMainNav ? <MainNavigation /> : null}
                    {showSettingSideNav ? <SettingsNavigation /> : null}
                    {/* We will use the global command dialog for global event registration with __hydra_commands__ */}
                    <CommandDialog />
                    <div className={styles.layoutContent}>
                      <AIShimmer />
                      <div
                        id="embedded-app-container"
                        className={styles.layoutContentInner}
                        style={{
                          opacity: appContainerFadedOut ? 0 : 1,
                        }}
                      >
                        {appContainerOutletRendered ? <Outlet /> : null}
                      </div>
                      {hasFeatureFlag(FeatureFlag.AiAssistantV2) && hasFeatureFlag(FeatureFlag.AiEnabled) ? (
                        <AssistantFooter />
                      ) : null}
                    </div>
                    <SidebarRoot />
                    <HelpSidebar />
                    {shouldShowRocket ? (
                      /* The Rocket component is experimental and isolated from the rest of the app,
                    So any errors that could happen within the component can be contained */
                      <ErrorBoundary fallback={<Fragment />}>
                        <RocketMissionControl />
                      </ErrorBoundary>
                    ) : null}
                  </div>
                </>
              ) : null}
              <SampleWorkspaceBorder />
            </SideNavProvider>
          </VideoLibraryProvider>
        </CopilotSessionProvider>
      </AuthProvider>
    </PermissionsProvider>
  );
}

function PermissionsProviderWrapper() {
  return (
    <PerformanceProvider>
      <AuthenticatedContent />
    </PerformanceProvider>
  );
}

export default function Authenticated() {
  return (
    <CurrentWorkspaceProvider>
      <PermissionsProviderWrapper />
    </CurrentWorkspaceProvider>
  );
}
